import EditIcon from "@mui/icons-material/Edit";
import { useCallback, useRef, useState } from "react";
import { PillToggle } from "@/components/campaignBuilder/design/PillToggle";
import RichEditor from "@/components/campaignBuilder/design/RichEditor";
import { TokenDropdown } from "@/components/campaignBuilder/design/TokenDropdown";
import { TokenMenu } from "@/components/campaignBuilder/design/TokenMenu";
import { EMAIL_FIELD_LIMITS, TOKENS } from "@/constants/campaignBuilder";
import type { ReminderEmailState } from "@/context/CampaignBuilderContext";
import { useSubjectAtMention } from "@/hooks/useSubjectAtMention";
import type { RichEditorRef } from "@/interfaces/CampaignBuilder";
import type { EmailMessageSection } from "@/types/types";
import { insertAtCursor } from "@/utils/messagePanel-helper";

const editBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--color-smoke)",
  borderRadius: 4,
  padding: 3,
  lineHeight: 1,
  cursor: "pointer",
  color: "var(--color-dark-dust)",
};

const fieldLabelClass =
  "font-lato text-[13px] font-bold text-rodeo-black uppercase tracking-[0.5px]";

interface ReminderCustomizePanelProps {
  state: ReminderEmailState;
  onChange: (v: Partial<ReminderEmailState>) => void;
}

export default function ReminderCustomizePanel({ state, onChange }: ReminderCustomizePanelProps) {
  const messageRef = useRef<RichEditorRef>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const handleMessageChange = useCallback(
    (html: string) => onChangeRef.current({ messageHtml: html }),
    [],
  );

  const [expandedSections, setExpandedSections] = useState<
    Omit<Record<EmailMessageSection, boolean>, "signature">
  >({
    subject: false,
    message: false,
  });

  const toggleSection = (section: Exclude<EmailMessageSection, "signature">) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const {
    subjectAtMention,
    handleSubjectChange,
    handleSubjectKeyDown,
    insertSubjectToken,
    dismissAtMention: dismissSubjectAtMention,
  } = useSubjectAtMention(
    subjectInputRef,
    (val) => onChange({ subject: val }),
    EMAIL_FIELD_LIMITS.subject,
  );

  return (
    <div className="flex flex-col">
      {/* ── Subject ── */}
      <div>
        {expandedSections.subject ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className={fieldLabelClass}>Subject Line</span>
                <TokenDropdown
                  onInsert={(token) => {
                    const el = subjectInputRef.current;
                    if (el)
                      insertAtCursor(
                        el,
                        token,
                        (v) => onChange({ subject: v }),
                        EMAIL_FIELD_LIMITS.subject,
                      );
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => toggleSection("subject")}
                style={editBtnStyle}
                title="Close"
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
            <div className="relative">
              <input
                ref={subjectInputRef}
                type="text"
                maxLength={EMAIL_FIELD_LIMITS.subject}
                value={state.subject}
                onChange={handleSubjectChange}
                onKeyDown={handleSubjectKeyDown}
                onBlur={dismissSubjectAtMention}
                autoComplete="off"
                placeholder="Enter reminder email subject"
                className="w-full h-10 px-3 border rounded-btn bg-white font-lato text-[14px] text-rodeo-black placeholder:text-graphite outline-none transition-colors border-smoke focus:border-dark-dust"
              />
              {subjectAtMention.active && subjectAtMention.items.length > 0 && (
                <TokenMenu
                  items={subjectAtMention.items}
                  activeIdx={subjectAtMention.activeIdx}
                  onInsert={insertSubjectToken}
                  className="absolute left-0 z-50"
                  style={{ top: "calc(100% + 4px)" }}
                />
              )}
            </div>
            <div className="flex justify-end mt-1">
              <span className="font-lato text-[11px] text-graphite">
                {state.subject.length} / {EMAIL_FIELD_LIMITS.subject}
              </span>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between">
            <span className={fieldLabelClass}>Subject Line</span>
            <button
              type="button"
              onClick={() => toggleSection("subject")}
              style={editBtnStyle}
              title="Edit subject line"
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        )}
      </div>

      {/* ── Message ── */}
      <div style={{ marginTop: 24 }}>
        {expandedSections.message ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className={fieldLabelClass}>Message</span>
                <TokenDropdown onInsert={(token) => messageRef.current?.insertText(token)} />
              </div>
              <button
                type="button"
                onClick={() => toggleSection("message")}
                style={editBtnStyle}
                title="Close"
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
            <RichEditor
              ref={messageRef}
              initialHtml={state.messageHtml}
              onChange={handleMessageChange}
              maxLength={EMAIL_FIELD_LIMITS.message}
              minHeight="140px"
              placeholder="Enter your reminder message"
              atMentionTokens={TOKENS}
              showAlign
            />
          </>
        ) : (
          <div className="flex items-center justify-between">
            <span className={fieldLabelClass}>Message</span>
            <button
              type="button"
              onClick={() => toggleSection("message")}
              style={editBtnStyle}
              title="Edit message"
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        )}
      </div>

      {/* ── Branding ── */}
      <div style={{ marginTop: 24 }}>
        <span className={`${fieldLabelClass} block mb-2`}>Branding</span>
        <p className="font-lato text-[12px] text-graphite leading-relaxed mb-3">
          Logo, hero banner, and CTA color are inherited from your main email. Toggle off to hide an
          element from this reminder only.
        </p>

        <div
          className="flex items-center justify-between py-2.5"
          style={{ borderTop: "1px solid var(--color-card-border)" }}
        >
          <span className="font-lato text-[13px] text-rodeo-black">Show partner logo</span>
          <PillToggle
            checked={state.showLogo}
            onChange={(v) => onChange({ showLogo: v })}
            aria-label="Show partner logo"
          />
        </div>

        <div
          className="flex items-center justify-between py-2.5"
          style={{
            borderTop: "1px solid var(--color-card-border)",
            borderBottom: "1px solid var(--color-card-border)",
          }}
        >
          <span className="font-lato text-[13px] text-rodeo-black">Show hero banner</span>
          <PillToggle
            checked={state.showBanner}
            onChange={(v) => onChange({ showBanner: v })}
            aria-label="Show hero banner"
          />
        </div>
      </div>

      {/* ── Shared note ── */}
      <div
        style={{
          marginTop: 24,
          padding: 14,
          background: "var(--color-clear-dust)",
          border: "1px solid var(--color-card-border)",
          borderRadius: 8,
          fontSize: 12,
          color: "var(--color-graphite)",
          lineHeight: 1.5,
          fontFamily: "var(--font-lato)",
        }}
      >
        <strong style={{ color: "var(--color-rodeo-black)" }}>Signature &amp; CTA color</strong>{" "}
        always sync from your main email — edit them in Step 3 (Design).
      </div>
    </div>
  );
}
